import { ContentState } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import { RawData } from './answerMarkdown';

/**
 * Picture alignment in the HTML editor (automations, Notification Center), which keeps react-draft-wysiwyg's own
 * picture. Its two converters disagree: draftjs-to-html saves the alignment on a wrapping
 * `<div style="text-align:...">`, while html-to-draftjs reads it back only from the picture's own float and turns the
 * wrapper into an empty line of its own. So every reopening lost the alignment, drawing the picture centred, and
 * left an empty line above it.
 */

// The wrapper draftjs-to-html writes, exactly: anything else is left to html-to-draftjs.
const ALIGNED_PICTURE = /<div style="text-align:(left|right|center|none);">(<img\b[^>]*?)(\/?)><\/div>/g;

// The library's centre is the alignment "none". A saved "none" never centred anything (see editorHtml), so it is
// left out and reads as left, where it was always shown.
const LIBRARY_ALIGNMENT: Record<string, string> = { center: 'none', right: 'right' };

/** The wrapper's alignment moved onto the picture, and the wrapper dropped. */
export const unwrapAlignedPictures = (html: string) =>
  html.replace(ALIGNED_PICTURE, (_, align, img, close) => `${img} data-align="${align}"${close}>`);

/** For html-to-draftjs: a picture with its alignment. None means left, where the HTML draws it. */
export const pictureFromHtml = (nodeName: string, node: HTMLElement) => {
  if (nodeName !== 'img' || !(node instanceof HTMLImageElement)) return undefined;
  const alignment = LIBRARY_ALIGNMENT[node.dataset.align || node.style.float] ?? 'left';
  return {
    type: 'IMAGE',
    mutability: 'MUTABLE',
    data: { src: node.getAttribute('src') || node.src, alt: node.alt, height: node.style.height, width: node.style.width, alignment }
  };
};

/** Saved HTML into the editor's content. */
export const editorContent = (html: string) => {
  const { contentBlocks, entityMap } = htmlToDraft(unwrapAlignedPictures(html ?? ''), pictureFromHtml);
  return ContentState.createFromBlockArray(contentBlocks, entityMap);
};

/**
 * The editor's content as saved HTML. draftjs-to-html writes the library's centre as `text-align:none` - not CSS, so
 * a centred picture went out on the left. Written as center, which pictureFromHtml reads back as none.
 */
export const editorHtml = (raw: RawData) =>
  draftToHtml(raw).replace(/<div style="text-align:none;"><img /g, '<div style="text-align:center;"><img ');
